/** Pure audio MIME candidate table and selection; the `isTypeSupported` probe is injected so this never touches `MediaRecorder` or `navigator`. */

/** Priority-ordered candidates: modern Opus, then Safari AAC and MP3, then legacy fallbacks. */
export const AUDIO_MIME_CANDIDATES = [
  "audio/webm;codecs=opus", // Chrome / Firefox modern
  "audio/ogg;codecs=opus", // Firefox legacy
  "audio/mp4;codecs=mp4a.40.2", // Safari AAC (from Angular table)
  "audio/mpeg", // Safari audio-recorder-polyfill result
  "audio/mp4",
  "audio/wav",
  "audio/mp3",
  "audio/webm",
  "audio/ogg",
] as const;

/** Probe signature — `MediaRecorder.isTypeSupported`, injected by the adapter. */
export type MimeProbe = (mimeType: string) => boolean;

/** Browser hints needed for codec preference branches. */
export interface AudioMimeEnv {
  /** `MediaRecorder.isTypeSupported`; `undefined` when `MediaRecorder` is absent (SSR/Node). */
  isTypeSupported?: MimeProbe;
  /** True on Firefox — prefers Ogg/Opus. */
  isFirefox?: boolean;
  /** True on Safari — prefers MP4/AAC, otherwise falls through to the normal candidates. */
  isSafari?: boolean;
}

/** Resolve the best audio MIME; no `MediaRecorder` or a false webm probe (iOS Safari, polyfill) gives `audio/mpeg`, Firefox prefers ogg opus, Safari prefers AAC, else the first supported or an empty string. */
export function negotiateAudioMime(env: AudioMimeEnv): string {
  const probe = env.isTypeSupported;
  if (!probe) return "audio/mpeg"; // SSR / no MediaRecorder

  // iOS Safari does not support audio/webm → polyfill MIME.
  if (!probe("audio/webm")) {
    return "audio/mpeg";
  }

  if (env.isSafari && probe("audio/mp4;codecs=mp4a.40.2")) {
    return "audio/mp4;codecs=mp4a.40.2";
  }

  if (env.isFirefox && probe("audio/ogg;codecs=opus")) {
    return "audio/ogg;codecs=opus";
  }

  for (const candidate of AUDIO_MIME_CANDIDATES) {
    if (probe(candidate)) return candidate;
  }

  return ""; // browser default
}
